import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Approx lat/lng for each community we serve.
import { LOCAL_CITIES as CITIES } from "@/data/local";

const hqIcon = L.divIcon({
  className: "",
  html: `<div style="display:grid;place-items:center;width:34px;height:34px;border-radius:9999px;background:#dc2626;border:3px solid #fff;box-shadow:0 4px 14px rgba(0,0,0,.35)">
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
  </div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

function FitView({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (focus) map.setView([focus.lat, focus.lng], 11);
    else map.fitBounds(L.latLngBounds(CITIES.map((c) => [c.lat, c.lng])), { padding: [40, 40] });
  }, [map, focus]);
  return null;
}

export default function CountyMap({ focusSlug, className = "h-[420px] sm:h-[520px] lg:h-[560px]" }) {
  const navigate = useNavigate();
  const focus = CITIES.find((c) => c.slug === focusSlug);
  return (
    <div
      className={`relative isolate z-0 w-full overflow-hidden rounded-xl border border-slate-200 shadow-sm ${className}`}
      data-testid={focus ? `county-map-${focus.slug}` : "county-map"}
    >
      <MapContainer
        center={focus ? [focus.lat, focus.lng] : [33.18, -96.66]}
        zoom={focus ? 11 : 10}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%", background: "#0f172a" }}
        className="z-0"
      >
        <TileLayer
          attribution='Tiles &copy; <a href="https://www.esri.com">Esri</a> &mdash; Esri, HERE, Garmin, &copy; OpenStreetMap contributors'
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
          maxZoom={18}
          eventHandlers={{
            tileloadstart: (e) => {
              e.tile.alt = "Street map of the Prosper garage door repair service area";
              e.tile.setAttribute("width", "256");
              e.tile.setAttribute("height", "256");
            },
          }}
        />
        <FitView focus={focus} />
        {CITIES.map((c) =>
          c.hq ? (
            <Marker
              key={c.slug}
              position={[c.lat, c.lng]}
              icon={hqIcon}
              eventHandlers={{ click: () => navigate(`/garage-door-repair/${c.slug}`) }}
            >
              <Tooltip direction="top" offset={[0, -16]} permanent className="map-tip map-tip--hq">
                Prosper · HQ
              </Tooltip>
            </Marker>
          ) : (
            <CircleMarker
              key={c.slug}
              center={[c.lat, c.lng]}
              radius={c.slug === focus?.slug ? 12 : 7}
              pathOptions={{ color: "#fff", weight: c.slug === focus?.slug ? 3 : 2, fillColor: "#dc2626", fillOpacity: 1 }}
              eventHandlers={{
                click: () => navigate(`/garage-door-repair/${c.slug}`),
                mouseover: (e) => e.target.setRadius(c.slug === focus?.slug ? 14 : 10),
                mouseout: (e) => e.target.setRadius(c.slug === focus?.slug ? 12 : 7),
              }}
            >
              <Tooltip direction="top" offset={[0, -8]} permanent={c.slug === focus?.slug} className="map-tip">{c.name}, TX</Tooltip>
            </CircleMarker>
          )
        )}
      </MapContainer>

      <div className={`${focus ? "hidden" : "flex"} pointer-events-none absolute bottom-3 left-3 z-[500] items-center gap-3 rounded-md bg-white/90 backdrop-blur px-3 py-1.5 text-[11px] font-semibold text-slate-700 shadow`}>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-600 ring-1 ring-white" /> Our HQ — Prosper</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full border-2 border-white bg-red-600" /> Service area</span>
      </div>
    </div>
  );
}
